import React from 'react';
import { Link2, MapPin, Plus, Ruler, ShieldCheck } from 'lucide-react';
import deleteIcon from '../../../shared/assets/svg/delete.svg';
import editIcon from '../../../shared/assets/svg/edit.svg';

const pillMint =
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EAF8F2] text-[#0F766E] max-w-full';
const pillLavender =
  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#EEF2FF] text-[#4F46E5] max-w-full';
const pillPeach =
  'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[13px] font-semibold bg-[#FFF4E8] text-[#B45309] max-w-full truncate';

/**
 * Card view for accommodations — matches land/property cards: avatar, type pill, location row, metric tags in two rows.
 */
const AccommodationCard = ({
  item,
  plotAreaUnit,
  measurementUnit,
  convertPlotArea,
  onEdit,
  onRequestDelete
}) => {
  const name = item?.name || 'Unnamed';
  const letterAvatar = (name.trim()[0] || 'A').toUpperCase();

  const accType = String(item?.accomodationType || '').toUpperCase();
  const typeLabel =
    accType === 'ALL'
      ? null
      : accType === 'INDEPENDENT'
        ? 'Independent'
        : accType === 'GROUP'
          ? 'Group'
          : accType === 'PARKING'
            ? 'Parking'
            : item?.accomodationType || '—';

  const formatArea = (val) => {
    if (val === null || val === undefined || val === '' || val === '---') return null;
    return convertPlotArea(val, measurementUnit || 'sq/ft', plotAreaUnit);
  };

  const plotDisp = formatArea(item?.plotArea);
  const buildDisp = formatArea(item?.buildUpArea);
  const carpetDisp = formatArea(item?.carpetArea);
  const superBuildUpDisp = formatArea(
    item?.superBuildUpArea ?? item?.super_build_up_area ?? item?.superBuiltUpArea
  );
  const linkedDisp = Number(item?.linkedUnits ?? 0).toLocaleString('en-IN');
  // const hasCad = Array.isArray(item?.cadFileLink) && item.cadFileLink.length > 0;
  const unitTypeLine = item?.unitType ? String(item.unitType) : 'Unit type not set';

  /** Metric pills; first often uses ShieldCheck. Rows: first two tags, then remainder (independent has four incl. linked units). */
  let tags = [];
  const linkedTag = {
    className: pillLavender,
    icon: <Link2 size={12} className="shrink-0" strokeWidth={2.5} />,
    text: `Linked units: ${linkedDisp}`
  };

  if (accType === 'INDEPENDENT') {
    tags = [
      {
        className: pillMint,
        icon: <ShieldCheck size={13} className="shrink-0" strokeWidth={2} />,
        text: `Plot: ${plotDisp != null ? `${plotDisp} ${plotAreaUnit}` : '—'}`
      },
      {
        className: pillLavender,
        icon: null,
        text: `Build: ${buildDisp != null ? `${buildDisp} ${plotAreaUnit}` : '—'}`
      },
      {
        className: pillPeach,
        icon: null,
        text: `Carpet: ${carpetDisp != null ? `${carpetDisp} ${plotAreaUnit}` : '—'}`
      },
      linkedTag
    ];
  } else if (accType === 'GROUP') {
    tags = [
      {
        className: pillMint,
        icon: <ShieldCheck size={13} className="shrink-0" strokeWidth={2} />,
        text: `Build: ${buildDisp != null ? `${buildDisp} ${plotAreaUnit}` : '—'}`
      },
      {
        className: pillLavender,
        icon: null,
        text: `Carpet: ${carpetDisp != null ? `${carpetDisp} ${plotAreaUnit}` : '—'}`
      },
      ...(superBuildUpDisp != null
        ? [
            {
              className: pillPeach,
              icon: null,
              text: `Super Build: ${superBuildUpDisp} ${plotAreaUnit}`
            }
          ]
        : []),
      linkedTag
    ];
  } else if (accType === 'PARKING') {
    tags = [
      {
        className: pillMint,
        icon: <ShieldCheck size={13} className="shrink-0" strokeWidth={2} />,
        text: `Build: ${buildDisp != null ? `${buildDisp} ${plotAreaUnit}` : '—'}`
      },
      linkedTag,
      {
        className: pillPeach,
        icon: <Ruler size={12} className="shrink-0" strokeWidth={2.5} />,
        text: item?.electrified ? `Power: ${item.electrified}` : 'Power: —'
      }
    ];
  } else {
    tags = [
      {
        className: pillMint,
        icon: <ShieldCheck size={13} className="shrink-0" strokeWidth={2} />,
        text: `Build: ${buildDisp != null ? `${buildDisp} ${plotAreaUnit}` : '—'}`
      },
      linkedTag
      // {
      //   className: pillPeach,
      //   icon: null,
      //   text: hasCad ? 'CAD: Attached' : 'CAD: Not attached'
      // }
    ];
  }

  const row1 = tags.slice(0, 2);
  const row2 = tags.slice(2);

  return (
    <article className="group bg-white w-full min-h-[220px] rounded-2xl border border-[#E5EAF1] p-4 flex flex-col gap-2.5 relative shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D8DEE6] motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-sm">
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(item?.id);
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#DCE3EA] bg-white text-[#4B5563] shadow-sm transition-colors hover:bg-[#F8FAFC] hover:border-[#CBD5E1] disabled:opacity-50"
          aria-label="Edit accommodation"
          disabled={!item?.id}>
          <img src={editIcon} alt="" className="h-[18px] w-[18px]" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRequestDelete?.(item?.id);
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full border border-[#DCE3EA] bg-white text-[#4B5563] shadow-sm transition-colors hover:bg-[#F8FAFC] hover:border-[#CBD5E1] disabled:opacity-50"
          aria-label="Delete accommodation"
          disabled={!item?.id}>
          <img src={deleteIcon} alt="" className="h-[18px] w-[18px]" />
        </button>
      </div>

      <div className="flex items-start gap-3 pr-24">
        <div className="h-14 w-14 shrink-0 rounded-xl border border-[#BFE6DE] bg-[#E7F2EF] flex items-center justify-center text-lg font-semibold text-[#2A565E]">
          {letterAvatar}
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className="text-[20px] leading-tight font-semibold text-[#111827] truncate"
            title={name}>
            {name}
          </h3>
          {typeLabel ? (
            <span className="mt-1 inline-flex max-w-full truncate rounded-full bg-[#F3E8FF] px-2.5 py-0.5 text-[12px] font-semibold text-[#7C3AED]">
              {typeLabel}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm text-[#6B7280]">
        <MapPin size={16} className="text-[#94A3B8] shrink-0" />
        <span className="truncate" title={unitTypeLine}>
          {unitTypeLine}
        </span>
      </div>

      <div className="mt-0.5 flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          {row1.map((tag, idx) => (
            <span key={idx} className={tag.className} title={tag.text}>
              {tag.icon}
              <span className="truncate">{tag.text}</span>
            </span>
          ))}
        </div>
        {row2.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {row2.map((tag, idx) => (
              <span key={idx} className={tag.className} title={tag.text}>
                {tag.icon}
                <span className="truncate">{tag.text}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {/* CAD file section on card — hidden for now (re-enable when needed)
      {hasCad ? (
        <div className="mt-1 border-t border-[#EEF2F5] pt-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8] mb-1.5">
            CAD file
          </p>
          <div className="flex flex-col gap-1">
            {item.cadFileLink.map((file, idx) => (
              <a
                key={file?.id ?? idx}
                href={file?.url}
                download={file?.name}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline truncate"
                title={file?.name}>
                <Download size={14} className="shrink-0 opacity-80" />
                <span className="truncate">{file?.name || 'Download'}</span>
              </a>
            ))}
          </div>
        </div>
      ) : null}
      */}
    </article>
  );
};

/**
 * First tile in card grid — opens the create accommodation flow.
 */
export const AddAccommodationCard = ({ onAdd }) => (
  <button
    type="button"
    onClick={onAdd}
    className="group flex min-h-[220px] w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#C7D3DE] bg-[#FAFBFC] p-6 text-center transition-all duration-300 hover:border-[#2B5C66] hover:bg-[#F0F7F8] hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2B5C66] focus-visible:ring-offset-2 motion-reduce:transition-none">
    <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#BFE6DE] bg-[#E7F2EF] text-[#2A565E] transition-colors group-hover:bg-[#D4EDE5] group-hover:border-[#9FD4C8]">
      <Plus size={28} strokeWidth={2.25} />
    </span>
    <span className="text-base font-semibold text-[#2B5C66]">Add accommodation</span>
    <span className="text-sm text-[#64748B] max-w-[220px] leading-snug">
      Create a new unit for this project
    </span>
  </button>
);

export default AccommodationCard;
